import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';

/**
 * PushSubscribeButton
 * Props:
 *  - jwt: string (Bearer token)
 *  - vapidKey: string (public VAPID key, base64 URL-safe)
 *  - className?: string
 *  - product?: 'trailblazer' | 'connect' | 'legacy' (copy tweaks)
 *  - onStateChange?: (state) => void  // { supported, permission, subscribed, ready }
 */
export default function PushSubscribeButton({
  jwt,
  vapidKey,
  className = '',
  product,
  onStateChange,
}) {
  const [supported, setSupported] = useState(true);
  const [ready, setReady] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // 🔊 prime only once
  const audioPrimedRef = useRef(false);
  const clickLockRef = useRef(false);

  const isIOS = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  }, []);

  const isIOSInstalled = useMemo(() => {
    if (!isIOS) return false;
    // iOS Safari push requires PWA installed (iOS 16.4+)
    return window.navigator.standalone === true;
  }, [isIOS]);

  // --- utils ---
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
  }

  async function getSWRegistrationSafe(timeoutMs = 8000) {
    // Race SW.ready with a timeout + getRegistration fallback to avoid hanging
    if (!('serviceWorker' in navigator)) return null;
    const timeout = new Promise((res) =>
      setTimeout(() => res(null), timeoutMs),
    );
    const readyP = (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        return reg || (await navigator.serviceWorker.getRegistration());
      } catch {
        return navigator.serviceWorker.getRegistration();
      }
    })();
    return (await Promise.race([readyP, timeout])) || null;
  }

  function primeAudioOnce() {
    if (audioPrimedRef.current) return;
    try {
      const a = new Audio('/sounds/notify.mp3'); // ensure this exists
      a.volume = 0;
      a.play()
        .then(() => {
          a.pause();
          a.currentTime = 0;
          audioPrimedRef.current = true;
        })
        .catch(() => {});
    } catch {}
  }

  async function syncSubscriptionToServer(reg, token) {
    try {
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return false;
      // Many servers treat this POST as upsert (idempotent)
      await axiosConn.post('/notifications/register-webpush', sub, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      return true;
    } catch {
      return false;
    }
  }

  function emitState(next = {}) {
    if (typeof onStateChange === 'function') {
      onStateChange({
        supported,
        permission,
        subscribed,
        ready,
        ...next,
      });
    }
  }

  // --- initial check + silent sync on mount ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ok =
        typeof window !== 'undefined' &&
        window.isSecureContext &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        typeof Notification !== 'undefined';

      if (mounted) setSupported(ok);
      if (!ok) {
        emitState({ supported: false });
        return;
      }

      if (mounted) setPermission(Notification.permission);

      const reg = await getSWRegistrationSafe();
      if (!reg) {
        if (mounted) setReady(false);
        emitState({ ready: false });
        return;
      }

      if (mounted) setReady(true);
      const sub = await reg.pushManager.getSubscription();
      const hasSub = !!sub;
      if (mounted) setSubscribed(hasSub);

      // Silent upsert in case server lost it (data cleared/pruned)
      if (hasSub && jwt) {
        await syncSubscriptionToServer(reg, jwt);
      }

      emitState({ ready: true, subscribed: hasSub });
    })();

    // Re-eval when tab regains focus (helps after login/redirect)
    const onVis = async () => {
      if (document.visibilityState !== 'visible') return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        if (mounted) setReady(true);
        const sub = await reg.pushManager.getSubscription();
        if (mounted) setSubscribed(!!sub);
        emitState({ ready: true, subscribed: !!sub });
      }
      if (mounted)
        setPermission(
          typeof Notification !== 'undefined'
            ? Notification.permission
            : 'default',
        );
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  // --- actions ---
  async function enablePush() {
    if (clickLockRef.current) return;
    clickLockRef.current = true;
    setMsg('');
    primeAudioOnce();

    try {
      if (!jwt) {
        setMsg('Please sign in to enable notifications.');
        return;
      }
      if (!supported) {
        setMsg('Push is not supported in this browser.');
        return;
      }
      if (isIOS && !isIOSInstalled) {
        setMsg('On iPhone/iPad: Add to Home Screen to enable push.');
        return;
      }
      if (!vapidKey) {
        setMsg('Missing VAPID key on client.');
        return;
      }

      setLoading(true);

      // Request permission (only prompts if "default")
      let perm =
        typeof Notification !== 'undefined'
          ? Notification.permission
          : 'denied';
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }
      setPermission(perm);
      if (perm !== 'granted') {
        setMsg(
          perm === 'denied'
            ? 'Notifications are blocked. Allow them in your browser site settings.'
            : 'Permission was not granted.',
        );
        return;
      }

      // Ensure SW ready
      const reg = await getSWRegistrationSafe();
      if (!reg) {
        setMsg('Service worker not ready. Reload and try again.');
        return;
      }

      // Ensure subscription (resubscribe if missing)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      // Send to backend (idempotent upsert)
      const resp = await axiosConn.post(
        '/notifications/register-webpush',
        sub,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
        },
      );

      if (!(resp?.status === 200 || resp?.status === 201)) {
        // Roll back local sub if server refused
        try {
          await sub.unsubscribe();
        } catch {}
        throw new Error(
          `Server rejected subscription (${resp?.status || 'unknown'})`,
        );
      }

      setSubscribed(true);
      setMsg('Push enabled. You’ll get real-time updates.');
      emitState({ subscribed: true, permission: 'granted' });
    } catch (err) {
      console.warn('[PushSubscribeButton] enable error:', err?.message || err);
      setMsg(err?.message || 'Failed to enable push.');
    } finally {
      setLoading(false);
      clickLockRef.current = false;
    }
  }

  async function disablePush() {
    if (clickLockRef.current) return;
    clickLockRef.current = true;
    setMsg('');

    try {
      if (!supported) return;
      setLoading(true);

      const reg = await getSWRegistrationSafe();
      if (!reg) {
        setMsg('Service worker not ready.');
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setSubscribed(false);
        setMsg('Push disabled.');
        emitState({ subscribed: false });
        return;
      }

      // Best-effort server prune (if your backend supports DELETE)
      try {
        await axiosConn.delete('/notifications/register-webpush', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          data: { endpoint: sub.endpoint },
        });
      } catch {
        /* ignore */
      }

      await sub.unsubscribe();

      setSubscribed(false);
      setMsg('Push disabled.');
      emitState({ subscribed: false });
    } catch (err) {
      console.warn('[PushSubscribeButton] disable error:', err?.message || err);
      setMsg('Failed to disable push.');
    } finally {
      setLoading(false);
      clickLockRef.current = false;
    }
  }

  function actionLabel() {
    if (!supported) return 'Not supported';
    if (permission === 'denied') return 'Notifications blocked';
    return subscribed ? 'Disable Push' : 'Enable Push Notifications';
  }

  const helpText = (() => {
    if (!supported) return 'Your browser does not support web push.';
    if (permission === 'denied') {
      return 'You’ve blocked notifications for this site. Use the lock icon → Site settings → Notifications → Allow.';
    }
    if (isIOS && !isIOSInstalled)
      return 'On iPhone/iPad, install to Home Screen to enable push.';
    if (!subscribed) {
      return product === 'trailblazer'
        ? 'Get instant hunt updates, check-ins, and rewards.'
        : 'Get instant likes, comments, and messages.';
    }
    return 'You’ll receive alerts even when the app is closed.';
  })();

  const disabled =
    loading || !supported || permission === 'denied' || (!ready && !subscribed);

  return (
    <div
      className={className}
      style={{ display: 'grid', gap: 8, maxWidth: 420 }}
    >
      <button
        type="button"
        onClick={subscribed ? disablePush : enablePush}
        disabled={disabled}
        aria-disabled={disabled}
        aria-live="polite"
        style={{
          padding: '10px 14px',
          borderRadius: 10,
          fontWeight: 700,
          border: '1px solid #ddd',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: subscribed ? '#f3f4f6' : '#facc15',
        }}
        title={
          permission === 'denied'
            ? 'Allow notifications in your browser site settings'
            : ''
        }
      >
        {loading ? 'Please wait…' : actionLabel()}
      </button>

      <div style={{ fontSize: 13, color: '#555' }}>{helpText}</div>
      {msg && <div style={{ fontSize: 13, color: '#8a2be2' }}>{msg}</div>}
    </div>
  );
}
